"use client";

import { useState } from 'react';
import Image from 'next/image';
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { v4 as uuidv4 } from 'uuid';
import { db, storage } from '@/lib/firebaseClient';

// PERUBAHAN: Ikon untuk tombol tambah/kurang
const PlusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
  </svg>
);

const TrashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
  </svg>
);


const UploadForm = () => {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [fileType, setFileType] = useState(''); // PERUBAHAN: State untuk tipe file (image/video)
  const [title, setTitle] = useState('');
  
  // PERUBAHAN: State author menjadi array of objects untuk menangani beberapa penulis
  const [authors, setAuthors] = useState([{ id: 1, name: '' }]);

  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // PERUBAHAN: Logika handle file diperbarui untuk video
  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      // PERUBAHAN: Batas ukuran dinaikkan menjadi 100MB untuk video
      if (selectedFile.size > 100 * 1024 * 1024) { 
        setError('Ukuran file terlalu besar. Maksimal 100MB.');
        return;
      }
      
      // PERUBAHAN: Cek tipe file
      if (selectedFile.type.startsWith('image/')) {
        setFileType('image');
      } else if (selectedFile.type.startsWith('video/')) {
        setFileType('video');
      } else {
        setError('Format file tidak didukung. Harap pilih gambar atau video.');
        return;
      }

      setFile(selectedFile);
      setPreviewUrl(URL.createObjectURL(selectedFile));
      setError('');
      setSuccess('');
    }
  };
  
  // PERUBAHAN: Fungsi-fungsi untuk mengelola daftar penulis
  const handleAuthorChange = (id, newName) => {
    setAuthors(authors.map(author => 
      author.id === id ? { ...author, name: newName } : author
    ));
  };

  const addAuthor = () => {
    // Menambahkan penulis baru dengan ID unik (timestamp)
    setAuthors([...authors, { id: Date.now(), name: '' }]);
  };

  const removeAuthor = (id) => {
    // Hanya hapus jika ada lebih dari satu penulis
    if (authors.length > 1) {
      setAuthors(authors.filter(author => author.id !== id));
    }
  };

  const resetForm = () => {
    setFile(null);
    setPreviewUrl('');
    setFileType('');
    setTitle('');
    setAuthors([{ id: 1, name: '' }]); // Reset ke satu penulis kosong
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // PERUBAHAN: Validasi untuk beberapa penulis
    const isAnyAuthorEmpty = authors.some(author => author.name.trim() === '');
    if (!file || !title || isAnyAuthorEmpty) {
      setError('Harap lengkapi judul, nama, dan pilih sebuah file media.');
      return;
    }

    setIsUploading(true);
    setError('');
    setSuccess('');

    try {
      const fileExtension = file.name.split('.').pop();
      const uniqueFileName = `${uuidv4()}.${fileExtension}`;
      
      // PERUBAHAN: Folder bisa dibedakan jika perlu, di sini kita tetap pakai 'karya_veo'
      const storagePath = file.type.startsWith('image/') ? `karya/${uniqueFileName}` : `karya_veo/${uniqueFileName}`;
      const storageRef = ref(storage, storagePath);

      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      // PERUBAHAN: Kirim array nama penulis ke Firestore
      const authorNames = authors.map(author => author.name.trim());
      const collectionName = file.type.startsWith('image/') ? "karya" : "karya_veo";


      await addDoc(collection(db, collectionName), {
        title: title,
        author: authorNames, // Menyimpan sebagai array
        mediaUrl: downloadURL, // Nama field lebih generik
        mediaType: fileType, // Menyimpan tipe media (image/video)
        voters: [],
        type: 1,
        voteCount: 0,
        createdAt: serverTimestamp()
      });

      setSuccess(`Karya "${title}" berhasil diupload!`);
      resetForm();

    } catch (err) {
      console.error("Error uploading file:", err);
      setError('Gagal mengupload karya. Silakan coba lagi.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="w-full bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg space-y-6">
      
      {/* PERUBAHAN: Area upload file sekarang mendukung video */}
      <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center cursor-pointer hover:border-indigo-500 transition-colors">
        {/* PERUBAHAN: `accept` diperbarui */}
        <input type="file" className="hidden" onChange={handleFileChange} accept="image/*,video/*" id="fileUpload" />
        <label htmlFor="fileUpload" className="cursor-pointer">
          {previewUrl ? (
            <div className="relative w-full h-48 rounded-md overflow-hidden bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
              {/* PERUBAHAN: Preview kondisional untuk gambar atau video */}
              {fileType === 'image' && (
                <Image src={previewUrl} alt="Preview" fill style={{ objectFit: 'contain' }} />
              )}
              {fileType === 'video' && (
                <video controls src={previewUrl} className="max-w-full max-h-full" />
              )}
            </div>
          ) : (
            <div>
              <p className="text-gray-500 dark:text-gray-400">
                <span className="font-semibold text-indigo-500">Klik untuk memilih</span> atau seret file ke sini
              </p>
              {/* PERUBAHAN: Teks deskripsi diperbarui */}
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Gambar & Video (PNG, JPG, MP4) hingga 100MB</p>
            </div>
          )}
        </label>
      </div>

      <div>
        <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Judul Karya</label>
        <input type="text" id="title" value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1 block w-full bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm p-3" placeholder="Contoh: Senja di Pesisir Pantai" />
      </div>
      
      {/* PERUBAHAN: Input dinamis untuk penulis */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nama Anggota Kelompok</label>
        {authors.map((author, index) => (
          <div key={author.id} className="flex items-center space-x-2 mb-2">
            <input 
              type="text" 
              value={author.name} 
              onChange={(e) => handleAuthorChange(author.id, e.target.value)} 
              className="block w-full bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm p-3" 
              placeholder={`Nama ${index + 1}`}
            />
            <button 
              type="button"
              onClick={() => removeAuthor(author.id)}
              disabled={authors.length <= 1}
              className="p-2 text-gray-500 hover:text-red-600 bg-gray-100 dark:bg-gray-600 dark:hover:text-red-400 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Hapus Penulis"
            >
              <TrashIcon />
            </button>
          </div>
        ))}
        <button 
          type="button" 
          onClick={addAuthor} 
          className="mt-2 flex items-center space-x-2 text-sm font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
        >
          <PlusIcon />
          <span>Tambah Anggota</span>
        </button>
      </div>

      {error && <p className="text-sm text-red-500">{error}</p>}
      {success && <p className="text-sm text-green-500">{success}</p>}

      <button type="submit" disabled={isUploading} className="w-full bg-indigo-600 text-white font-bold py-3 px-4 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-indigo-300 disabled:cursor-not-allowed transition-all duration-300">
        {isUploading ? 'Mengunggah...' : 'Upload Karya'}
      </button>
    </form>
  );
};

export default UploadForm;